/**
 * Pourquoi cette cellule demande une revue — chunk 9.AL.1.
 *
 * ## Ce que ce composant corrige
 *
 * `needs_review` était un booléen : il disait qu'il fallait vérifier, jamais ce
 * qu'il fallait aller voir. Un conseiller devant 34 cellules signalées n'avait
 * aucun moyen de distinguer « la source dans le document n'est pas confirmée » de
 * « deux lectures se contredisent » — deux gestes de vérification très différents.
 *
 * ## Ce qu'il n'est pas
 *
 * Ni un statut de cellule (9.AB.1), ni une qualification de preuve (9.AK.1), ni un
 * score (9.AJ.1). Il explique **un drapeau existant**, il n'en crée aucun et n'en
 * retire aucun.
 *
 * Aucun enum brut n'atteint le DOM : chaque motif a sa phrase, et un motif inconnu
 * retombe sur une formulation neutre plutôt que d'afficher sa clé.
 *
 * Rien de ceci ne va au client — le rapport 9.AI.1 est inchangé.
 */

import { useTranslation } from "react-i18next";

/** Les motifs que la consolidation sait produire. Ensemble fermé côté serveur. */
const MOTIFS_CONNUS = [
  "missing_citation",
  "insufficient_confidence",
  "disagreement",
  "invalid_value",
  "forced_human_confirmation",
  /** Champ hors du contrat de la passe : conservé et signalé, jamais canonique. */
  "out_of_contract",
] as const;

export function ReviewReasonHint({
  reasons,
  className,
}: {
  reasons: string[] | null | undefined;
  className?: string;
}) {
  const { t } = useTranslation();

  // `null` = motif non calculé (ligne antérieure au chunk), et surtout pas
  // « aucun motif ». Afficher « aucune raison » sur un héritage laisserait croire
  // que le signalement est infondé.
  if (!reasons || reasons.length === 0) return null;

  const phrases = reasons.map((r) =>
    (MOTIFS_CONNUS as readonly string[]).includes(r)
      ? t(`review_reason.${r}`)
      : t("review_reason.unknown"),
  );

  return (
    <span className={className ?? "mt-0.5 block text-[11px] text-amber-700"}>
      {/* Plusieurs motifs peuvent coexister : une citation absente ET une
          confiance faible se cumulent, et les deux se réparent différemment. */}
      {phrases.join(" · ")}
    </span>
  );
}
